interface HeightTarget {
  getBoundingClientRect(): { height: number }
}

interface ResizeListenerTarget {
  addEventListener(type: 'resize', listener: () => void): void
  removeEventListener(type: 'resize', listener: () => void): void
}

type ResizeObserverLike = { observe(target: never): void; disconnect(): void }
type ResizeObserverCtor = new (callback: () => void) => ResizeObserverLike

export interface ObserveElementHeightOptions {
  win?: ResizeListenerTarget
  ResizeObserverImpl?: ResizeObserverCtor
}

/**
 * Reports the element's rounded-up height now, on window resize, and — when the
 * browser supports it — on every ResizeObserver change. Zero heights are ignored so
 * a CSS fallback stays in effect. Returns a cleanup function.
 */
export function observeElementHeight(
  element: HeightTarget,
  onHeight: (height: number) => void,
  options: ObserveElementHeightOptions = {},
): () => void {
  const win: ResizeListenerTarget | undefined =
    'win' in options ? options.win : typeof window === 'undefined' ? undefined : window
  const Observer: ResizeObserverCtor | undefined =
    'ResizeObserverImpl' in options
      ? options.ResizeObserverImpl
      : typeof ResizeObserver === 'undefined'
        ? undefined
        : (ResizeObserver as unknown as ResizeObserverCtor)

  const update = () => {
    const height = Math.ceil(element.getBoundingClientRect().height)
    if (height > 0) onHeight(height)
  }

  update()
  win?.addEventListener('resize', update)
  const observer = Observer ? new Observer(update) : null
  observer?.observe(element as never)

  return () => {
    observer?.disconnect()
    win?.removeEventListener('resize', update)
  }
}
